import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Trash2, ArrowUp, ArrowDown, Loader2, Eye, EyeOff,
  ImageIcon, Video as VideoIcon, CheckCircle2, Plus,
} from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime";

/**
 * Media manager for the coach dashboard.
 *
 * Upload UX (redesigned for one-tap simplicity):
 *   - Whole card is a drag-and-drop zone.
 *   - One prominent button opens the OS picker with `multiple` enabled.
 *   - Each file gets a live thumbnail with a real progress bar during upload.
 *   - When the batch finishes, a clear success banner appears.
 *
 * Coaches can still reorder, edit captions, publish/unpublish, and delete
 * items AFTER upload — no forms are required BEFORE upload.
 */
export default function MediaManager({ coachEmail }) {
  const [items, setItems] = useState([]);
  const [limits, setLimits] = useState({ max_items: 20 });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [uploads, setUploads] = useState([]); // { key, name, previewUrl, progress, status }
  const [successCount, setSuccessCount] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const successTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media`);
      setItems(data.media || []);
      setLimits(data.limits || { max_items: 20 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load your media.");
    } finally {
      setLoading(false);
    }
  }, [coachEmail]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (successTimerRef.current) clearTimeout(successTimerRef.current); }, []);

  const updateUpload = (key, patch) =>
    setUploads((cur) => cur.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  const uploadOne = async (entry) => {
    const form = new FormData();
    form.append("file", entry.file);
    await api.post(
      `/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
          updateUpload(entry.key, { progress: pct });
        },
      },
    );
  };

  const onFilesChosen = async (files) => {
    if (!files?.length) return;
    // Cap by remaining slots so we never blow the limit and confuse the coach.
    const remaining = Math.max(0, (limits.max_items || 20) - items.length);
    if (remaining <= 0) {
      toast.error(`You've hit the ${limits.max_items || 20}-item cap.`);
      return;
    }
    const filesArr = Array.from(files).slice(0, remaining);
    if (filesArr.length < files.length) {
      toast.message(`Uploading the first ${filesArr.length} — portfolio cap reached.`);
    }

    const entries = filesArr.map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`,
      file,
      name: file.name,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      isVideo: file.type.startsWith("video/"),
      progress: 0,
      status: "pending",
    }));
    setUploads((cur) => [...cur, ...entries]);
    setSuccessCount(0);

    let ok = 0;
    // Sequential keeps proxy happy and progress readable on mobile.
    for (const entry of entries) {
      updateUpload(entry.key, { status: "uploading" });
      try {
        await uploadOne(entry);
        updateUpload(entry.key, { status: "done", progress: 100 });
        ok += 1;
      } catch (err) {
        updateUpload(entry.key, {
          status: "error",
          error: err?.response?.data?.detail || "Upload failed",
        });
        toast.error(`${entry.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }

    // Refresh gallery once at the end — one round-trip, no flicker per item.
    await load();

    if (ok > 0) {
      setSuccessCount(ok);
      // Auto-clear the success banner after a bit so the UI settles.
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccessCount(0), 6000);
      toast.success(`${ok} photo${ok === 1 ? "" : "s"} added to your portfolio`);
    }

    // Give the browser a beat to render, then drop the transient tiles.
    setTimeout(() => {
      setUploads((cur) => {
        cur.forEach((u) => u.previewUrl && URL.revokeObjectURL(u.previewUrl));
        return cur.filter((u) => u.status === "error");
      });
    }, 900);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openPicker = () => fileInputRef.current?.click();

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading || atCap) return;
    const files = e.dataTransfer?.files;
    if (files?.length) onFilesChosen(files);
  };

  const setCaption = async (id, caption) => {
    setSavingId(id);
    try {
      await api.patch(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media/${id}`, { caption });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save caption.");
    } finally {
      setSavingId(null);
    }
  };

  const togglePublish = async (item) => {
    const next = !item.is_published;
    setSavingId(item.id);
    setItems((cur) => cur.map((m) => (m.id === item.id ? { ...m, is_published: next } : m)));
    try {
      await api.patch(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media/${item.id}`, { is_published: next });
      toast.success(next ? "Published — clients can see it now." : "Unpublished.");
    } catch (err) {
      setItems((cur) => cur.map((m) => (m.id === item.id ? { ...m, is_published: !next } : m)));
      toast.error(err?.response?.data?.detail || "Couldn't update publish state.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media? This can't be undone.")) return;
    setItems((cur) => cur.filter((m) => m.id !== id));
    try {
      await api.delete(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
      load();
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await api.patch(
        `/api/coach/dashboard/${encodeURIComponent(coachEmail)}/media/reorder`,
        { order: next.map((m) => m.id) },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save order.");
      load();
    }
  };

  const atCap = items.length >= (limits.max_items || 20);
  const uploading = uploads.some((u) => u.status === "uploading" || u.status === "pending");
  const totalInFlight = uploads.filter((u) => u.status !== "done").length;

  return (
    <section
      data-testid="media-manager"
      className={`mt-8 rounded-3xl border bg-white p-6 md:p-8 transition-colors ${
        dragOver ? "border-slate-900 bg-slate-50" : "border-slate-200"
      }`}
      onDragOver={(e) => { e.preventDefault(); if (!uploading && !atCap) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl md:text-2xl tracking-tight text-slate-900">Portfolio</h2>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-[52ch]">
            Show clients what you're about — photos and short videos of your training, results, or setup.
            Items stay <b>hidden</b> until you hit <b>Publish</b>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12.5px] text-slate-500">
            {items.length}/{limits.max_items || 20}
          </span>
        </div>
      </div>

      {/* Success banner */}
      {successCount > 0 && (
        <div
          data-testid="media-success-banner"
          className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 inline-flex items-center gap-2 text-emerald-800 text-[13.5px]"
        >
          <CheckCircle2 className="w-4 h-4" />
          <span><b>{successCount}</b> photo{successCount === 1 ? "" : "s"} added to your portfolio.</span>
        </div>
      )}

      {/* Primary upload CTA — big, obvious, drag-and-drop friendly */}
      <div className="mt-5">
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading || atCap}
          data-testid="media-upload-btn"
          className={`w-full rounded-2xl border-2 border-dashed transition-all
            ${atCap
              ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
              : dragOver
                ? "border-slate-900 bg-slate-50 text-slate-900"
                : "border-slate-300 bg-white hover:border-slate-900 hover:bg-slate-50 text-slate-700"}
            px-6 py-8 md:py-10 inline-flex flex-col items-center justify-center gap-2`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-[15px] font-semibold">
                Uploading {totalInFlight} file{totalInFlight === 1 ? "" : "s"}…
              </span>
              <span className="text-[12.5px] text-slate-500">Hang tight — this only takes a moment.</span>
            </>
          ) : atCap ? (
            <>
              <ImageIcon className="w-6 h-6" />
              <span className="text-[15px] font-semibold">Portfolio full</span>
              <span className="text-[12.5px]">Remove an item to add more.</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-slate-900 text-white inline-flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </div>
              <span className="text-[15px] font-semibold text-slate-900">Add photos or videos</span>
              <span className="text-[12.5px] text-slate-500">
                Tap to pick multiple, or drop them here. JPG, PNG, MP4 up to 60&nbsp;MB each.
              </span>
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => onFilesChosen(e.target.files)}
          data-testid="media-file-input"
        />
      </div>

      {/* In-flight uploads with per-file progress */}
      {uploads.length > 0 && (
        <ul data-testid="media-upload-progress" className="mt-4 space-y-2">
          {uploads.map((u) => (
            <li
              key={u.key}
              data-testid={`media-upload-row-${u.key}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                {u.previewUrl ? (
                  <img src={u.previewUrl} alt="" className="w-full h-full object-cover" />
                ) : u.isVideo ? (
                  <VideoIcon className="w-5 h-5 text-slate-400" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13px] font-medium text-slate-800">{u.name}</span>
                  <span className={`text-[11.5px] font-semibold ${
                    u.status === "done" ? "text-emerald-600"
                      : u.status === "error" ? "text-rose-600"
                      : "text-slate-500"
                  }`}>
                    {u.status === "done" ? "Uploaded"
                      : u.status === "error" ? (u.error || "Failed")
                      : `${u.progress}%`}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      u.status === "done" ? "bg-emerald-500"
                        : u.status === "error" ? "bg-rose-500"
                        : "bg-slate-900"
                    }`}
                    style={{ width: `${u.status === "error" ? 100 : u.progress}%` }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Existing items */}
      {loading ? (
        <div className="mt-8 flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : items.length === 0 && uploads.length === 0 ? (
        <div
          data-testid="media-empty"
          className="mt-6 text-center text-[13px] text-slate-400"
        >
          No media yet — your first uploads will appear here.
        </div>
      ) : items.length > 0 ? (
        <ul data-testid="media-list" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, idx) => (
            <li
              key={item.id}
              data-testid={`media-item-${item.id}`}
              className="rounded-2xl border border-slate-200 overflow-hidden bg-slate-50"
            >
              <div className="relative aspect-video bg-slate-100">
                {item.type === "video" ? (
                  <video
                    src={item.url}
                    controls
                    className="w-full h-full object-cover bg-black"
                    preload="metadata"
                  />
                ) : (
                  <img src={item.url} alt={item.caption || "Coach media"} className="w-full h-full object-cover" loading="lazy" />
                )}
                <span
                  className={`absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full ${
                    item.is_published
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-900/85 text-white"
                  }`}
                >
                  {item.is_published ? "Live" : "Draft"}
                </span>
                <span className="absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-white/95 text-slate-700 inline-flex items-center gap-1">
                  {item.type === "video" ? <VideoIcon className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                  {item.type}
                </span>
              </div>
              <div className="p-3 space-y-2">
                <input
                  type="text"
                  defaultValue={item.caption || ""}
                  placeholder="Caption (optional)"
                  maxLength={240}
                  onBlur={(e) => {
                    const next = e.target.value.trim().slice(0, 240);
                    if (next !== (item.caption || "")) setCaption(item.id, next);
                  }}
                  data-testid={`media-caption-${item.id}`}
                  className="w-full text-[13.5px] px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:border-slate-400"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      title="Move up"
                      data-testid={`media-move-up-${item.id}`}
                      className="h-8 w-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"
                    ><ArrowUp className="w-4 h-4" /></button>
                    <button
                      type="button"
                      onClick={() => move(idx, +1)}
                      disabled={idx === items.length - 1}
                      title="Move down"
                      data-testid={`media-move-down-${item.id}`}
                      className="h-8 w-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"
                    ><ArrowDown className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => togglePublish(item)}
                      disabled={savingId === item.id}
                      data-testid={`media-publish-${item.id}`}
                      className={`h-8 px-3 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 ${
                        item.is_published
                          ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          : "bg-slate-900 text-white hover:bg-slate-800"
                      }`}
                    >
                      {item.is_published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {item.is_published ? "Unpublish" : "Publish"}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(item.id)}
                      title="Delete"
                      data-testid={`media-delete-${item.id}`}
                      className="h-8 w-8 rounded-full inline-flex items-center justify-center text-rose-600 hover:bg-rose-50"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
